import { Socket } from "phoenix";
import "./main.css";
import { Elm } from "./Main.elm";

// TODO: remove this after it's been deployed for a while
navigator.serviceWorker.getRegistrations().then((registrations) => {
  for (let registration of registrations) {
    registration.unregister();
  }
});

const playerIdKey = "settle-it:playerId";
const playerNameKey = "playerName";

const envApiBaseUrl = process.env.ELM_APP_API_BASE_URL;

const urlParams = new URLSearchParams(window.location.search);
const gameCodeFromUrl = urlParams.get("game") || null;
const botLobbyFromUrl = urlParams.get("bots") === "true";
const skipGameSetup = !!process.env.ELM_APP_SKIP_GAME_SETUP || botLobbyFromUrl;

const serverBaseUrl = envApiBaseUrl || "settle-it.gigalixirapp.com";

const socketProtocol = window.location.protocol === "https:" ? "wss" : "ws";

let playerId = localStorage.getItem(playerIdKey);
const savedPlayerName = localStorage.getItem(playerNameKey);

const uuidv4 = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const generateGameCode = () => {
  let result = "";
  const characters = "abcdefghijklmnopqrstuvwxyz";
  const charactersLength = characters.length;
  for (let i = 0; i < 4; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

if (!playerId) {
  playerId = uuidv4();
  localStorage.setItem(playerIdKey, playerId);
}

// player_id is passed as a socket-level connect param so UserSocket.connect/3
// can validate it (UUID check) before any channel join is attempted. It is NOT
// re-sent in join payloads — the server reads it from socket.assigns.
const socket = new Socket(`${socketProtocol}://${serverBaseUrl}/socket`, {
  params: { player_id: playerId },
});

socket.onError((err) => {
  console.error("[socket] connection error", err);
});

socket.onClose(() => {
  console.warn("[socket] connection closed");
});

socket.connect();

let gameChannel;

const app = Elm.Main.init({
  node: document.getElementById("root"),
  flags: {
    playerId: playerId,
    savedPlayerName: savedPlayerName,
    skipGameSetup: skipGameSetup,
    gameCode: gameCodeFromUrl,
  },
});

const attachGameChannelListeners = (gc) => {
  gc.on("game:updated", (gameState) => {
    app.ports.gameUpdated.send(gameState);
  });

  gc.on("bodies:updated", (bodiesState) => {
    app.ports.bodiesUpdated.send(bodiesState);
  });
};

app.ports.joinGame.subscribe(({ gameId, topic }) => {
  // Leave any existing channel before creating a new one — otherwise Phoenix.js
  // accumulates stale channels for the same topic, and the server may reject the
  // new join because the socket is already subscribed to that topic.
  if (gameChannel) {
    gameChannel.leave();
    gameChannel = null;
  }

  // player_id is no longer included in the join payload — the server reads it
  // from socket.assigns (set during the socket-level connect handshake).
  const joinPayload = topic ? { topic } : {};
  const gameCode = (gameId || generateGameCode()).toLowerCase();

  gameChannel = socket.channel(`game:${gameCode}`, joinPayload);

  attachGameChannelListeners(gameChannel);

  gameChannel
    .join()
    .receive("ok", () => {
      if (skipGameSetup) {
        startDebugBot(gameCode);
      }
    })
    .receive("error", (resp) => {
      console.error("[channel] join error", resp);
    })
    .receive("timeout", () => {
      console.warn("[channel] join timeout — Phoenix will retry automatically");
    });
});

app.ports.startGame.subscribe(() => {
  gameChannel.push("start_game", {});
});

app.ports.restartGame.subscribe(() => {
  gameChannel.push("restart_game", {});
});

app.ports.copyToClipboard.subscribe((text) => {
  navigator.clipboard.writeText(text).catch(() => {});
});

app.ports.copyGameLink.subscribe(async (gameCode) => {
  const url = `${window.location.origin}${window.location.pathname}?game=${gameCode}`;
  if (navigator.share) {
    try {
      await navigator.share({ title: "Settle It", url });
      app.ports.gameLinkAction.send("shared");
    } catch (e) {
      // user cancelled — no feedback
    }
  } else {
    try {
      await navigator.clipboard.writeText(url);
      app.ports.gameLinkAction.send("copied");
    } catch (e) {}
  }
});

app.ports.createTeam.subscribe(({ name }) => {
  gameChannel.push("create_team", { name });
});

app.ports.deleteTeam.subscribe((teamId) => {
  gameChannel.push("delete_team", { team_id: teamId });
});

app.ports.joinTeam.subscribe(({ teamId }) => {
  gameChannel.push("player_join_team", { team_id: teamId });
});

app.ports.updatePlayerName.subscribe(({ name }) => {
  localStorage.setItem(playerNameKey, name);
  gameChannel.push("player_update_name", { name });
});

let cachedPlayerMesh = null;
fetch("player-mesh.json")
  .then((r) => r.text())
  .then((text) => {
    cachedPlayerMesh = text;
  })
  .catch(() => {});

app.ports.requestPlayerMesh.subscribe(() => {
  if (cachedPlayerMesh) {
    app.ports.playerMeshLoaded.send(cachedPlayerMesh);
  } else {
    fetch("player-mesh.json")
      .then((r) => r.text())
      .then((text) => {
        cachedPlayerMesh = text;
        app.ports.playerMeshLoaded.send(text);
      })
      .catch(() => {});
  }
});

app.ports.requestJump.subscribe((_playerId) => {
  gameChannel.push("player_jump", {});
});

app.ports.requestMove.subscribe(({ x, y }) => {
  gameChannel.push("player_move", { x, y });
});

app.ports.requestRotate.subscribe(({ angle }) => {
  gameChannel.push("player_rotate", { angle });
});

app.ports.requestShoot.subscribe(({ position, linvel }) => {
  gameChannel.push("player_shoot", { position, linvel });
});

// Connects bot players to the same game channel and automates lobby setup so
// the real player lands directly in a game without manual team/start steps.
// Only used when ELM_APP_SKIP_GAME_SETUP is set or ?bots=true is in the URL.
let activeBotSession = null;

function startDebugBot(gameCode) {
  if (activeBotSession && activeBotSession.gameCode === gameCode) {
    // Reuse existing bots — reset their joined flags and re-wire the player
    // channel automation (gameChannel is a new object after play-again).
    activeBotSession.bots.forEach((bot) => {
      bot.joined = false;
    });
    runPlayerChannelAutomation(
      activeBotSession.bots,
      activeBotSession.teamNames,
    );
    return;
  }

  const MAX_BOTS = 16;
  // Randomize between 2 and 4 teams
  const numTeams = 2 + Math.floor(Math.random() * 3);

  const teamNames =
    numTeams === 2
      ? ["Me", "Them"]
      : [
          "Me",
          ...Array.from({ length: numTeams - 1 }, (_, i) => `Them ${i + 1}`),
        ];

  // Round-robin assignment ensures bot 0 is always on the player's team
  const botTeamAssignments = Array.from(
    { length: MAX_BOTS },
    (_, i) => i % numTeams,
  );

  // Drive each bot through lobby setup.
  const bots = Array.from({ length: MAX_BOTS }, (_, i) => {
    const botSocket = new Socket(
      `${socketProtocol}://${serverBaseUrl}/socket`,
      {
        params: { player_id: uuidv4() },
      },
    );
    botSocket.connect();
    const bot = {
      channel: botSocket.channel(`game:${gameCode}`, { is_bot: true }),
      teamIndex: botTeamAssignments[i],
      name: `Bot ${i + 1}`,
      joined: false,
    };

    bot.channel.on("game:updated", (state) => {
      if (bot.joined) return;
      if (state.teams.length < teamNames.length) return;
      const targetTeam = state.teams.find(
        (t) => t.cause === teamNames[bot.teamIndex],
      );
      if (targetTeam) {
        bot.joined = true;
        bot.channel.push("player_join_team", { team_id: targetTeam.id });
      }
    });

    bot.channel
      .join()
      .receive("ok", () => {
        bot.channel.push("player_update_name", { name: bot.name });
      })
      .receive("error", (resp) => {
        console.error(`[bot ${i + 1}] join error`, resp);
      });

    return bot;
  });

  activeBotSession = { gameCode, bots, teamNames };
  runPlayerChannelAutomation(bots, teamNames);
}

// Drive the real player's channel through lobby setup automatically.
function runPlayerChannelAutomation(bots, teamNames) {
  const MAX_BOTS = bots.length;
  let teamsCreated = false;
  let playerTeamJoined = false;
  let gameStarted = false;

  gameChannel.on("game:updated", (state) => {
    if (gameStarted) return;

    if (!teamsCreated) {
      teamsCreated = true;
      teamNames.forEach((name) => gameChannel.push("create_team", { name }));
      return;
    }

    if (!playerTeamJoined) {
      if (state.teams.length === teamNames.length) {
        const myTeam = state.teams.find((t) => t.cause === "Me");
        if (myTeam) {
          playerTeamJoined = true;
          gameChannel.push("player_join_team", { team_id: myTeam.id });
        }
      }
      return;
    }

    const allBotsPresent = state.players.length === MAX_BOTS + 1;
    const unassigned = state.players.filter((p) => !p.team_id);
    if (allBotsPresent && unassigned.length === 0) {
      gameStarted = true;
      gameChannel.push("start_game", {});
    }
  });
}

window.onbeforeunload = () => {
  if (gameChannel) {
    gameChannel.leave();
  }
};

// Prevent all zoom gestures. iOS 10+ ignores viewport user-scalable=no, so
// JS prevention is required. gesturestart/change/end are Safari-specific events
// for pinch-to-zoom; touchmove with multiple touches catches other browsers.
document.addEventListener("gesturestart", (e) => e.preventDefault(), {
  passive: false,
});
document.addEventListener("gesturechange", (e) => e.preventDefault(), {
  passive: false,
});
document.addEventListener("gestureend", (e) => e.preventDefault(), {
  passive: false,
});

document.addEventListener(
  "touchmove",
  (e) => {
    if (e.touches.length > 1) e.preventDefault();
  },
  { passive: false },
);

// Prevent double-tap zoom.
let lastTouchEnd = 0;
const touchTapDelta = 300;
document.addEventListener(
  "touchend",
  (event) => {
    const now = Date.now();
    if (now - lastTouchEnd <= touchTapDelta) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  },
  { passive: false },
);
